<template>
  <div>
    <context-bar />
    <div class="d-flex align-center mb-2 flex-wrap">
      <div class="text-subtitle-1">Sections</div>
      <span class="ml-3 text-caption grey--text">Drag to reorder. Width is out of 12 columns (12 = full row, 6 = half, 4 = a third).</span>
      <v-spacer />
      <v-menu offset-y>
        <template v-slot:activator="{ on }">
          <v-btn small color="primary" v-on="on"><v-icon left small>mdi-plus</v-icon>Add section</v-btn>
        </template>
        <v-list dense>
          <v-list-item v-for="t in sectionTypes" :key="t.value" @click="add(t.value)">
            <v-list-item-icon><v-icon small>{{ t.icon }}</v-icon></v-list-item-icon>
            <v-list-item-title>{{ t.text }}</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
    </div>

    <v-row>
      <!-- page canvas: drag blocks from the palette, reorder by dragging, resize from the right edge -->
      <v-col cols="12" md="7">
        <div class="d-flex align-center flex-wrap mb-2">
          <draggable
            :list="palette" :group="{ name: 'sections', pull: 'clone', put: false }" :sort="false" :clone="makeSection" class="d-flex flex-wrap"
          >
            <div v-for="t in palette" :key="t.value" class="rb-pal" :title="`Drag onto the page: ${t.text}`">
              <v-icon x-small class="mr-1">{{ t.icon }}</v-icon>{{ t.text }}
            </div>
          </draggable>
          <v-spacer />
          <v-btn x-small outlined :loading="state.sample.loading" @click="showRealData"><v-icon left x-small>mdi-database-eye-outline</v-icon>Show real data</v-btn>
        </div>
        <div class="text-caption grey--text mb-2">
          Drag a block from the palette onto the page - drag a block by its heading to move it - drag its right edge to make it wider or narrower.
          <span v-if="state.sample.error" class="error--text">Could not load data: {{ state.sample.error }}</span>
        </div>
        <div ref="canvas" class="rb-canvas">
          <draggable v-model="sections" group="sections" handle=".rb-block-head" ghost-class="rb-ghost" class="rb-canvas-row" @add="onAdd">
            <div
              v-for="(s, i) in sections" :key="s.id" class="rb-cell" :style="{ flex: `0 0 calc(${((s.width || 12) / 12) * 100}% - 8px)` }"
              @click="selected = i"
            >
              <section-block :section="s" :selected="selected === i" :datasets="sampleDatasets" :class="{ 'rb-hidden': s.hidden }" />
              <div class="rb-resize" title="Drag to resize" @mousedown.stop.prevent="startResize($event, s)" />
              <v-btn icon x-small class="rb-del" :title="s.hidden ? 'Show on screen' : 'Hide on screen'" style="right: 26px" @click.stop="$set(s, 'hidden', !s.hidden)"><v-icon x-small>{{ s.hidden ? 'mdi-eye-off-outline' : 'mdi-eye-outline' }}</v-icon></v-btn>
              <v-btn icon x-small class="rb-del" title="Remove this block" @click.stop="remove(i)"><v-icon x-small>mdi-close</v-icon></v-btn>
            </div>
          </draggable>
          <div v-if="!sections.length" class="rb-canvas-empty">Drag a block here to start designing the page</div>
        </div>
      </v-col>

      <!-- section properties -->
      <v-col cols="12" md="5">
        <v-card v-if="current" outlined>
          <v-card-text>
            <v-row dense>
              <v-col cols="12" sm="6"><v-text-field v-model="current.title" outlined dense hide-details label="Heading" /></v-col>
              <v-col cols="6" sm="3"><v-select v-model="current.type" :items="sectionTypes" item-text="text" item-value="value" outlined dense hide-details label="Type" @change="typeChanged" /></v-col>
              <v-col cols="6" sm="3"><v-select v-model="current.width" :items="widths" outlined dense hide-details label="Width (of 12)" /></v-col>
              <v-col cols="12"><v-checkbox :input-value="!!current.hidden" dense hide-details class="mt-0" label="Hide on screen (everything below moves up; prints are set in the print designer)" @change="$set(current, 'hidden', $event)" /></v-col>
            </v-row>

            <!-- tiles -->
            <div v-if="current.type === 'kpi'" class="mt-4">
              <div class="d-flex align-center mb-2"><b>Tiles</b><v-spacer />
                <v-btn x-small text @click="addItem"><v-icon left x-small>mdi-plus</v-icon>Add tile</v-btn></div>
              <v-row v-for="(it, k) in current.items" :key="k" dense align="center">
                <v-col cols="12" sm="3"><v-text-field v-model="it.label" outlined dense hide-details label="Label" /></v-col>
                <v-col cols="6" sm="2"><v-select v-model="it.dataset" :items="datasetNames" outlined dense hide-details label="Dataset" /></v-col>
                <v-col cols="6" sm="2"><field-picker v-model="it.field" :dataset="it.dataset" label="Column" /></v-col>
                <v-col cols="6" sm="2"><v-select v-model="it.aggregate" :items="aggregates" clearable outlined dense hide-details label="Combine" /></v-col>
                <v-col cols="5" sm="2"><v-select v-model="it.format" :items="formats" clearable outlined dense hide-details label="Format" /></v-col>
                <v-col cols="1"><v-btn icon x-small @click="current.items.splice(k, 1)"><v-icon small>mdi-close</v-icon></v-btn></v-col>
              </v-row>
            </div>

            <!-- table -->
            <div v-if="current.type === 'table'" class="mt-4">
              <v-row dense align="center">
                <v-col cols="12" sm="4"><v-select v-model="current.dataset" :items="datasetNames" outlined dense hide-details label="Dataset" /></v-col>
                <v-col cols="6" sm="3"><v-text-field v-model.number="current.pageSize" type="number" outlined dense hide-details label="Rows per page" placeholder="25" /></v-col>
                <v-col cols="6" sm="3"><v-checkbox v-model="current.showTotals" dense hide-details label="Show totals row" class="mt-0" /></v-col>
              </v-row>
              <v-row dense align="center" class="mt-1">
                <v-col cols="6" sm="3"><v-switch :input-value="current.sortable !== false" dense hide-details label="Allow sorting" class="mt-0" @change="setFlag('sortable', $event)" /></v-col>
                <v-col cols="6" sm="3"><v-switch :input-value="current.searchable !== false" dense hide-details label="Search box" class="mt-0" @change="setFlag('searchable', $event)" /></v-col>
                <v-col cols="6" sm="3"><v-switch :input-value="current.downloadable !== false" dense hide-details label="Download (CSV / Excel)" class="mt-0" @change="setFlag('downloadable', $event)" /></v-col>
                <v-col v-if="current.sortable !== false" cols="6" sm="3" />
                <v-col v-if="current.sortable !== false" cols="8" sm="4"><field-picker :value="(current.defaultSort || {}).field" :dataset="current.dataset" label="Sorted by default" @input="setDefaultSort('field', $event)" /></v-col>
                <v-col v-if="current.sortable !== false" cols="4" sm="2"><v-checkbox :input-value="(current.defaultSort || {}).desc" dense hide-details label="Descending" class="mt-0" @change="setDefaultSort('desc', $event)" /></v-col>
              </v-row>
              <div class="text-caption grey--text">With sorting on, untick "Sortable" on a column to block just that one.</div>
              <div class="d-flex align-center mt-3 mb-1">
                <b>Columns</b>
                <span class="ml-2 text-caption grey--text">Leave empty to show every column of the dataset.</span>
                <v-spacer />
                <v-btn x-small text :loading="detecting" @click="fillColumns">Detect columns from the query</v-btn>
                <v-btn x-small text @click="addColumn"><v-icon left x-small>mdi-plus</v-icon>Add column</v-btn>
              </div>
              <draggable v-model="current.columns" handle=".rb-col-handle">
                <v-card v-for="(c, k) in current.columns || []" :key="k" outlined class="mb-1">
                  <v-card-text class="py-2">
                    <v-row dense align="center">
                      <v-col cols="auto"><v-icon small class="rb-col-handle" style="cursor: grab">mdi-drag</v-icon></v-col>
                      <v-col cols="6" sm="3"><field-picker v-model="c.field" :dataset="current.dataset" label="Column" /></v-col>
                      <v-col cols="6" sm="3"><v-text-field v-model="c.label" outlined dense hide-details label="Heading" /></v-col>
                      <v-col cols="4" sm="2"><v-select v-model="c.format" :items="formats" clearable outlined dense hide-details label="Format" /></v-col>
                      <v-col cols="4" sm="1"><v-select v-model="c.align" :items="['left', 'right', 'center']" clearable outlined dense hide-details label="Align" /></v-col>
                      <v-col cols="4" sm="2"><v-select v-model="c.total" :items="['sum', 'avg', 'count', 'none']" clearable outlined dense hide-details label="Total" /></v-col>
                      <v-col cols="auto"><v-btn icon x-small @click="current.columns.splice(k, 1)"><v-icon small>mdi-close</v-icon></v-btn></v-col>
                    </v-row>
                    <v-row dense class="mt-1" align="center">
                      <v-col cols="6" sm="4"><v-select v-model="c.link" :items="linkTypes" clearable outlined dense hide-details label="Opens record (double / middle click)" @change="linkChanged(c)" /></v-col>
                      <v-col v-if="c.link" cols="6" sm="4"><field-picker v-model="c.linkField" :dataset="current.dataset" label="Column holding the record id" /></v-col>
                      <v-col cols="6" sm="3"><v-checkbox :input-value="c.sortable !== false" dense hide-details label="Sortable" class="mt-0" :disabled="current.sortable === false" @change="$set(c, 'sortable', !!$event)" /></v-col>
                      <v-col cols="6" sm="3"><v-checkbox v-model="c.hidden" dense hide-details label="Hide on screen (keep in exports)" class="mt-0" /></v-col>
                    </v-row>
                  </v-card-text>
                </v-card>
              </draggable>
            </div>

            <!-- chart -->
            <div v-if="current.type === 'chart'" class="mt-4">
              <v-row dense>
                <v-col cols="12" sm="4"><v-select v-model="current.dataset" :items="datasetNames" outlined dense hide-details label="Dataset" /></v-col>
                <v-col cols="6" sm="3"><v-select v-model="current.chart" :items="chartTypes" outlined dense hide-details label="Chart type" /></v-col>
                <v-col cols="6" sm="3"><field-picker v-model="current.x" :dataset="current.dataset" label="Labels (x axis)" /></v-col>
                <v-col cols="6" sm="2"><v-select v-model="current.valueFormat" :items="formats" clearable outlined dense hide-details label="Value format" /></v-col>
              </v-row>
              <v-checkbox v-if="current.chart === 'bar' || current.chart === 'line' || current.chart === 'area'" v-model="current.stacked" dense hide-details label="Stack the series" />
              <v-expansion-panels flat class="mt-2" style="border: 1px solid rgba(128,128,128,.3)">
                <v-expansion-panel>
                  <v-expansion-panel-header class="py-0" style="min-height: 40px">
                    <span><b>Your own ECharts JSON</b>
                      <v-chip v-if="current.echartsOption" x-small class="ml-2" color="primary">{{ current.echartsMode === 'replace' ? 'replaces the form' : 'merged over the form' }}</v-chip></span>
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <div class="text-caption mb-2">
                      Paste an ECharts <code>option</code> (from the ECharts editor or anywhere). <b>Merge</b> lays it over the chart made by the form above
                      (objects merge, arrays are replaced); <b>Replace</b> uses only your JSON, with this section's rows supplied as <code>dataset.source</code>
                      so series can use <code>encode</code>, e.g. <code>{"series":[{"type":"bar","encode":{"x":"day","y":"total"}}]}</code>.
                    </div>
                    <v-radio-group :value="current.echartsMode || 'merge'" row dense hide-details class="mt-0 mb-2" @change="setEchartsMode">
                      <v-radio label="Merge over the form" value="merge" /><v-radio label="Replace the form" value="replace" />
                    </v-radio-group>
                    <v-textarea
                      v-model="echartsText" outlined dense rows="8" auto-grow spellcheck="false" style="font-family: monospace; font-size: 12px"
                      :error-messages="echartsError" label="ECharts option JSON" @input="applyEcharts"
                    />
                    <v-btn x-small text @click="clearEcharts">Clear</v-btn>
                    <v-btn x-small text @click="formatEcharts">Format</v-btn>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>
              <div class="d-flex align-center mt-2 mb-1"><b>Series</b><v-spacer />
                <v-btn x-small text @click="addSeries"><v-icon left x-small>mdi-plus</v-icon>Add series</v-btn></div>
              <v-row v-for="(se, k) in current.series || []" :key="k" dense align="center">
                <v-col cols="6" sm="5"><field-picker v-model="se.field" :dataset="current.dataset" label="Values column" /></v-col>
                <v-col cols="5" sm="5"><v-text-field v-model="se.label" outlined dense hide-details label="Legend name" /></v-col>
                <v-col cols="1"><v-btn icon x-small @click="current.series.splice(k, 1)"><v-icon small>mdi-close</v-icon></v-btn></v-col>
              </v-row>
            </div>

            <!-- text -->
            <div v-if="current.type === 'text'" class="mt-4">
              <v-textarea v-model="current.content" outlined dense rows="4" auto-grow counter="5000" label="Text shown on the report" />
            </div>
          </v-card-text>
        </v-card>
        <v-alert v-else type="info" dense text>Select a section to edit it.</v-alert>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import axios from 'axios'
import { mapGetters } from 'vuex'
import draggable from 'vuedraggable'
import ContextBar from './ContextBar.vue'
import FieldPicker from './FieldPicker.vue'
import SectionBlock from './SectionBlock.vue'
import { chartStarterJson } from '../rbUtil'
import { state, container, datasetNames, knownColumns, uniqueName, loadSample, SECTION_TYPES, FORMATS, RECORD_LINK_TYPES } from './editorState'

export default {
  name: 'RbLayoutTab',
  components: { draggable, ContextBar, FieldPicker, SectionBlock },
  data () {
    return {
      selected: 0,
      state,
      palette: SECTION_TYPES,
      detecting: false,
      sectionTypes: SECTION_TYPES,
      formats: FORMATS,
      linkTypes: RECORD_LINK_TYPES,
      aggregates: ['first', 'sum', 'count', 'avg', 'max', 'min'],
      chartTypes: [{ text: 'Bars', value: 'bar' }, { text: 'Line', value: 'line' }, { text: 'Area', value: 'area' }, { text: 'Pie', value: 'pie' }],
      widths: [12, 9, 8, 7, 6, 5, 4, 3],
      echartsText: '',
      echartsError: ''
    }
  },
  computed: {
    ...mapGetters(['getEndpoint']),
    sections: {
      get () { return container().ui.sections },
      set (v) { container().ui.sections = v }
    },
    current () { return this.sections[this.selected] || null },
    datasetNames () { return datasetNames() },
    // real figures for the blocks, once "Show real data" has been pressed for this part of the report
    sampleDatasets () { return state.sample.result && state.sample.ctx === state.ctx ? state.sample.result.datasets : null }
  },
  watch: {
    'state.ctx' () { this.selected = 0 },
    selected: { immediate: true, handler () { this.loadEcharts() } },
    sections () { this.loadEcharts() }
  },
  methods: {
    // a new block of the given type, as dropped from the palette (or the Add menu)
    newSection (type) {
      const ds = this.datasetNames[0]
      const s = { id: uniqueName('s', this.sections.map((x) => x.id)), type, title: '', width: 12 }
      if (type === 'kpi') s.items = [{ label: 'Total', dataset: ds, field: '', format: 'number' }]
      if (type === 'table') Object.assign(s, { dataset: ds, columns: [], showTotals: false })
      if (type === 'chart') Object.assign(s, { dataset: ds, chart: 'bar', x: '', series: [{ field: '', label: '' }] })
      if (type === 'text') s.content = ''
      return s
    },
    // ---- table behaviour ----
    setFlag (key, on) {
      this.$set(this.current, key, !!on)
      if (key === 'sortable' && !on) this.$delete(this.current, 'defaultSort')
    },
    setDefaultSort (key, v) {
      const d = { ...(this.current.defaultSort || {}) }
      if (key === 'field') d.field = v || ''
      else d.desc = !!v
      if (!d.field) this.$delete(this.current, 'defaultSort')
      else this.$set(this.current, 'defaultSort', d)
    },
    // ---- your own ECharts JSON ----
    loadEcharts () {
      const o = this.current && this.current.type === 'chart' ? this.current.echartsOption : null
      this.echartsText = o ? JSON.stringify(o, null, 2) : ''
      this.echartsError = ''
    },
    applyEcharts (text) {
      if (!this.current) return
      if (!text || !text.trim()) { this.$delete(this.current, 'echartsOption'); this.echartsError = ''; return }
      try {
        const o = JSON.parse(text)
        if (!o || typeof o !== 'object' || Array.isArray(o)) throw new Error('must be a JSON object, like {"series": [...]}')
        this.$set(this.current, 'echartsOption', o)
        this.echartsError = ''
      } catch (e) {
        this.echartsError = `Not valid JSON yet: ${e.message}`
      }
    },
    // switching to Replace starts from the chart the form currently makes, so it can be edited rather than written from scratch
    setEchartsMode (mode) {
      this.$set(this.current, 'echartsMode', mode)
      if (mode === 'replace' && !this.current.echartsOption) {
        this.echartsText = chartStarterJson(this.current)
        this.applyEcharts(this.echartsText)
      }
    },
    clearEcharts () { this.echartsText = ''; this.applyEcharts('') },
    formatEcharts () { if (!this.echartsError && this.current && this.current.echartsOption) this.echartsText = JSON.stringify(this.current.echartsOption, null, 2) },
    makeSection (item) { return this.newSection(item.value) },
    onAdd (evt) { this.selected = evt.newIndex },
    showRealData () { loadSample(this.getEndpoint) },
    startResize (e, s) {
      const canvas = this.$refs.canvas
      if (!canvas) return
      const col = canvas.clientWidth / 12
      const startX = e.clientX
      const startW = s.width || 12
      const move = (ev) => { s.width = Math.max(1, Math.min(12, Math.round(startW + (ev.clientX - startX) / col))) }
      const up = () => { window.removeEventListener('mousemove', move); window.removeEventListener('mouseup', up) }
      window.addEventListener('mousemove', move)
      window.addEventListener('mouseup', up)
    },
    typeText (t) { return (SECTION_TYPES.find((x) => x.value === t) || {}).text || t },
    icon (t) { return (SECTION_TYPES.find((x) => x.value === t) || {}).icon || 'mdi-square' },
    known (ds) { return knownColumns(ds) },
    add (type) {
      this.sections.push(this.newSection(type))
      this.selected = this.sections.length - 1
    },
    remove (i) {
      this.sections.splice(i, 1)
      if (this.selected >= this.sections.length) this.selected = Math.max(0, this.sections.length - 1)
    },
    typeChanged () {
      const s = this.current
      const ds = this.datasetNames[0]
      if (s.type === 'kpi' && !s.items) this.$set(s, 'items', [{ label: 'Total', dataset: ds, field: '', format: 'number' }])
      if (s.type === 'table' && !s.dataset) this.$set(s, 'dataset', ds)
      if (s.type === 'chart') { if (!s.dataset) this.$set(s, 'dataset', ds); if (!s.chart) this.$set(s, 'chart', 'bar'); if (!s.series) this.$set(s, 'series', [{ field: '', label: '' }]) }
      if (s.type === 'text' && s.content === undefined) this.$set(s, 'content', '')
    },
    addItem () { this.current.items.push({ label: '', dataset: this.datasetNames[0], field: '', format: 'number' }) },
    addColumn () {
      if (!this.current.columns) this.$set(this.current, 'columns', [])
      this.current.columns.push({ field: '', label: '' })
    },
    // run the dataset's query once to learn its column names, then list them as table columns
    async fillColumns () {
      const ds = container().datasets.find((d) => d.name === this.current.dataset)
      if (!ds) return
      this.detecting = true
      try {
        const res = await axios.post(`${this.getEndpoint}/api/rb-builder/preview-sql`, { sql: ds.sql, params: state.tpl.params, values: state.previewParams })
        this.$set(state.lastColumns, `${state.ctx}.${ds.name}`, res.data.data.columns)
        this.$set(this.current, 'columns', res.data.data.columns.map((c) => ({ field: c, label: c })))
      } finally {
        this.detecting = false
      }
    },
    linkChanged (c) { if (!c.link) this.$delete(c, 'linkField') },
    addSeries () {
      if (!this.current.series) this.$set(this.current, 'series', [])
      this.current.series.push({ field: '', label: '' })
    }
  }
}
</script>

<style scoped>
.rb-pal { border: 1px dashed rgba(128, 128, 128, 0.8); border-radius: 4px; padding: 3px 8px; margin: 0 6px 4px 0; font-size: 12px; cursor: grab; background: #fff; }
.rb-canvas { background: #eceff1; border-radius: 6px; padding: 8px; min-height: 160px; }
.rb-canvas-row { display: flex; flex-wrap: wrap; align-items: stretch; min-height: 120px; }
.rb-cell { position: relative; margin: 4px; cursor: pointer; min-width: 60px; }
.rb-resize { position: absolute; top: 0; right: -5px; bottom: 0; width: 10px; cursor: ew-resize; border-radius: 4px; }
.rb-resize:hover { background: rgba(25, 118, 210, 0.35); }
.rb-del { position: absolute; top: 2px; right: 4px; opacity: 0; }
.rb-cell:hover .rb-del { opacity: 1; }
.rb-ghost { opacity: 0.4; }
.rb-hidden { opacity: 0.4; filter: grayscale(1); }
.rb-canvas-empty { text-align: center; color: #888; padding: 36px 0; }
.rb-sketch { border: 1px dashed rgba(128, 128, 128, 0.7); border-radius: 4px; min-height: 36px; cursor: pointer; }
.rb-sketch-on { background: rgba(25, 118, 210, 0.15); border-color: #1976d2; }
</style>
